import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "../api";
import { useI18n } from "../i18n";
import {
  CheckCircle2, Clock, Loader2, AlertTriangle, Mail, FileArchive, ShieldCheck,
  Wallet, Search, Send, Edit3, Download, Package,
} from "lucide-react";

// Map event kind → icon + label
const EVENT_META = {
  created:              { icon: Wallet,        label: "Commande reçue",                 tone: "cyan" },
  payment_verified:     { icon: ShieldCheck,   label: "Paiement vérifié on-chain",      tone: "emerald" },
  mq5_personalized:     { icon: FileArchive,   label: "Fichier .mq5 personnalisé",      tone: "cyan" },
  email_operator_sent:  { icon: Send,          label: "Email envoyé à notre équipe",    tone: "cyan" },
  email_client_sent:    { icon: Mail,          label: "Email de confirmation envoyé",   tone: "cyan" },
  ex5_uploaded:         { icon: Package,       label: ".ex5 compilé et prêt",           tone: "emerald" },
  delivered:            { icon: CheckCircle2,  label: "Commande livrée",                tone: "emerald" },
  status_changed:       { icon: Edit3,         label: "Statut modifié",                 tone: "amber" },
  error:                { icon: AlertTriangle, label: "Erreur",                         tone: "red" },
};

const TONE_CLASSES = {
  cyan:    "bg-cyan-400/15 text-cyan-300 border-cyan-400/40",
  emerald: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40",
  amber:   "bg-amber-400/15 text-amber-300 border-amber-400/40",
  red:     "bg-red-400/15 text-red-300 border-red-400/40",
};

export default function Track() {
  const { orderId } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/track/${orderId}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Commande introuvable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="track-loading">
        <Loader2 className="h-6 w-6 animate-spin inline" />
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="track-error">
        <h1 className="text-3xl font-bold mb-4">Commande introuvable</h1>
        <p className="text-zinc-400 mb-6">{err}</p>
        <Link to="/" className="btn-primary inline-block">Retour à l'accueil</Link>
      </div>
    );
  }

  const events = data.events || [];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="page-track">
      <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">Suivi de commande</div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter mb-1">
        Commande #{data.short_id}
      </h1>
      <p className="text-zinc-400 mb-8">
        {data.first_name && <>Bonjour {data.first_name}, voici l'avancement de votre commande.</>}
      </p>

      <div className="glass rounded-2xl p-6 mb-6">
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 mb-1">Plan</div>
            <div className="text-zinc-200">{data.plan_label || "—"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 mb-1">Montant</div>
            <div className="text-zinc-200 font-mono">{data.price_display || "—"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 mb-1">Statut</div>
            <div className="inline-flex items-center gap-1.5 text-zinc-200">
              <span className={`inline-block h-2 w-2 rounded-full ${data.status === "delivered" ? "bg-emerald-400" : data.status === "failed" ? "bg-red-400" : "bg-cyan-400 animate-pulse"}`} />
              <code className="text-xs font-mono">{data.status}</code>
            </div>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-5">Timeline</div>
        {events.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">Aucun événement enregistré pour le moment.</div>
        ) : (
          <ol className="relative space-y-5 pl-8" data-testid="track-timeline">
            <span className="absolute left-3.5 top-2 bottom-2 w-px bg-white/[0.08]" aria-hidden />
            {events.map((ev, i) => {
              const meta = EVENT_META[ev.kind] || { icon: Clock, label: ev.kind, tone: "cyan" };
              const Icon = meta.icon;
              const cls = TONE_CLASSES[meta.tone] || TONE_CLASSES.cyan;
              return (
                <li key={i} className="relative" data-testid={`track-event-${ev.kind}`}>
                  <div className={`absolute -left-8 top-0 w-7 h-7 rounded-full border flex items-center justify-center ${cls}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="text-sm font-semibold">{meta.label}</div>
                  {ev.message && <div className="text-xs text-zinc-400 mt-0.5">{ev.message}</div>}
                  <div className="text-[11px] text-zinc-500 mt-1 font-mono">
                    {new Date(ev.at).toLocaleString()}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {data.ex5_available && (
          <a
            href={`${API}/orders/${data.order_id}/download`}
            className="btn-primary mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm"
            data-testid="track-download-ex5"
          >
            <Download className="h-4 w-4" /> Télécharger mon .ex5
          </a>
        )}
      </div>

      <div className="text-center mt-8 text-xs text-zinc-500">
        Conservez cette URL pour suivre votre commande à tout moment.
      </div>
    </div>
  );
}
